import type { Router } from 'vue-router'

import { useCartStore } from '@/stores/cart'
import { useMainStore } from '@/stores/main'

import { resetStores } from '@/composables/resetStores'
import { CrossTabMessageType, postCrossTabMessage } from '@/lib/crossTabChannel'
import { PRIMARY_MARKET_LANDING_ROUTE } from '@/router/routeName'

/**
 * A tab whose cart's order completed elsewhere goes to the subject's market landing rather than
 * reloading into stores the completing tab has cleared. Rationale: iteration 14 of
 * `docs/features/2026.10.01-deferred-intent-payment-hold/implementation-plans/2026.10.01-001-deferred-intent-payment-hold.md`
 */

const NOTICE_KEY = 'orderCompletedNotice'

let leaving = false

/**
 * Tells every other tab that the order on `cartId` completed, so those showing that cart leave for
 * their landing. Post it before clearing the stores, so it arrives ahead of the clear's `cart_updated`.
 */
export function announceOrderCompleted(cartId: string | null | undefined): void {
  if (cartId) postCrossTabMessage(CrossTabMessageType.OrderCompleted, cartId)
}

/**
 * Leaves for the subject's market landing with a full load, so it starts clean, and has that page
 * say why. With `clear`, this tab found the paid cart itself, so it tells the other tabs and clears
 * the saved stores first; a tab that was told leaves them to the tab that told it.
 */
export function leaveCompletedOrder(router: Router, { clear }: { clear: boolean }): void {
  if (leaving) return
  leaving = true

  // Read before the clear empties them.
  const { cadyMarket, cadySessionToken } = useMainStore()
  const landing =
    cadyMarket && cadySessionToken
      ? router.resolve({
          name: PRIMARY_MARKET_LANDING_ROUTE[cadyMarket],
          query: { s: cadySessionToken }
        }).href
      : '/'

  if (clear) {
    announceOrderCompleted(useCartStore().cartData?.cartId)
    resetStores()
  }

  try {
    sessionStorage.setItem(NOTICE_KEY, '1')
  } catch {
    // The landing loads without saying why.
  }

  window.location.href = landing
}

/** Whether this tab is on its way to the landing, when other tabs' reloads would only get in the way. */
export function isLeavingCompletedOrder(): boolean {
  return leaving
}

/** Whether this page load came from `leaveCompletedOrder`. Answers once. */
export function takeOrderCompletedNotice(): boolean {
  try {
    const pending = sessionStorage.getItem(NOTICE_KEY) !== null
    sessionStorage.removeItem(NOTICE_KEY)
    return pending
  } catch {
    return false
  }
}
